import { Grid } from "@mui/material";
import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { PartnerMetricItem } from "Infrastructure/Api/Api";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { chartSeriesColors, colors } from "Infrastructure/Themes/Colors";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { useReportingPartnersQuery } from "Reporting/Api/Queries/useReportingPartnersQuery";
import { ChartCard } from "./ChartCard";
import { ChartEmptyState } from "./ChartEmptyState";
import { PeriodSelector, type ReportingPeriod } from "./PeriodSelector";

const c = colors();

const CHART_COLORS = chartSeriesColors.slice(0, 3);

const TOOLTIP_STYLE = {
  contentStyle: {
    backgroundColor: c.BackgroundTile,
    border: `1px solid ${c.Border}`,
    borderRadius: 8,
  },
  labelStyle: { color: c.TextSecondary },
  itemStyle: { color: c.White },
  cursor: { fill: "rgba(255, 255, 255, 0.05)" },
} as const;

const AXIS_TICK = { fill: c.TextSecondary, fontSize: 12 } as const;

type PartnerChartProps = {
  title: string;
  data: PartnerMetricItem[];
  isLoading: boolean;
  color: string;
  isCurrency?: boolean;
  tooltipLabel: string;
  action?: React.ReactNode;
};

const PartnerChart = ({
  title,
  data,
  isLoading,
  color,
  isCurrency,
  tooltipLabel,
  action,
}: PartnerChartProps): React.ReactNode => {
  const chartHeight = Math.max(200, data.length * 40 + 40);

  return (
    <ChartCard
      title={title}
      isLoading={isLoading}
      height={chartHeight}
      action={action}
    >
      {data.length === 0 ? (
        <ChartEmptyState />
      ) : (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
            <CartesianGrid stroke={c.Border} strokeDasharray="3 3" />
            <XAxis
              type="number"
              tick={AXIS_TICK}
              axisLine={{ stroke: c.Border }}
              tickLine={false}
              tickFormatter={
                isCurrency
                  ? (v: number) => formatCurrency(v, CurrencyCode.EUR, 0)
                  : undefined
              }
            />
            <YAxis
              type="category"
              dataKey="partnerName"
              tick={AXIS_TICK}
              axisLine={{ stroke: c.Border }}
              tickLine={false}
              width={120}
            />
            <Tooltip
              {...TOOLTIP_STYLE}
              formatter={(value) => [
                isCurrency
                  ? formatCurrency(Number(value), CurrencyCode.EUR, 0)
                  : Number(value),
                tooltipLabel,
              ]}
            />
            <Bar
              dataKey="value"
              fill={color}
              radius={[0, 4, 4, 0]}
              maxBarSize={28}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
};

export const PartnerMetricsCharts = (): React.ReactNode => {
  const { t } = useResource();
  const [projectsPeriod, setProjectsPeriod] = useState<ReportingPeriod>(null);
  const [jobsPeriod, setJobsPeriod] = useState<ReportingPeriod>(null);
  const [spendPeriod, setSpendPeriod] = useState<ReportingPeriod>(null);

  const { data: projectsData, isLoading: projectsLoading } =
    useReportingPartnersQuery(projectsPeriod);
  const { data: jobsData, isLoading: jobsLoading } =
    useReportingPartnersQuery(jobsPeriod);
  const { data: spendData, isLoading: spendLoading } =
    useReportingPartnersQuery(spendPeriod);

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
        <PartnerChart
          title={t(Resources.Reporting.TopPartnersByProjects)}
          data={projectsData?.topPartnersByProjects ?? []}
          isLoading={projectsLoading}
          color={CHART_COLORS[0]}
          tooltipLabel={t(Resources.Common.Projects)}
          action={
            <PeriodSelector
              value={projectsPeriod}
              onChange={setProjectsPeriod}
            />
          }
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
        <PartnerChart
          title={t(Resources.Reporting.TopPartnersByJobs)}
          data={jobsData?.topPartnersByJobs ?? []}
          isLoading={jobsLoading}
          color={CHART_COLORS[1]}
          tooltipLabel={t(Resources.Reporting.Jobs)}
          action={
            <PeriodSelector value={jobsPeriod} onChange={setJobsPeriod} />
          }
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
        <PartnerChart
          title={t(Resources.Reporting.TopPartnersBySpend)}
          data={spendData?.topPartnersBySpend ?? []}
          isLoading={spendLoading}
          color={CHART_COLORS[2]}
          isCurrency
          tooltipLabel={t(Resources.Reporting.Revenue)}
          action={
            <PeriodSelector value={spendPeriod} onChange={setSpendPeriod} />
          }
        />
      </Grid>
    </Grid>
  );
};
